import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { toneDot } from '../campaign-status.js';
import { describeSeat, seatLook } from '../linkedin-seat.js';
import {
  defaultRepToAssign,
  equalShares,
  mailboxOptions,
  missingAccountsPhrase,
  missingChannelsForRep,
  type MailboxOption,
  toggledAccount,
  toggledRep,
  withShare,
} from './logic/sales-reps.js';
import type { ChannelFallback, SalesRep, SalesRepAssignment } from './types.js';

export interface WizCampaignTypeProps {
  emailAccountsByAie: Record<string, string[]>;
  linkedinAccountIdsByAie: Record<string, string[]>;
  aiEmployeeAssignments: SalesRepAssignment[];
  crmAutoSync: boolean;
  /**
   * False while an existing campaign's real data (including its actual
   * `aiEmployeeAssignments`) is still being fetched by the wizard's own load
   * effect. `aiEmployeeAssignments` reads as `[]` for that entire window,
   * indistinguishable from a genuinely-unassigned campaign — the auto-default
   * effect below must not act until this is true, or it can race the load and
   * silently pick an arbitrary default Sales Rep, on the live campaign, in
   * place of the real one. Always true for a brand-new draft (nothing to wait
   * for). See the effect's own comment for the full history.
   */
  assignmentsReady: boolean;
  /**
   * Channels the campaign's sequence actually sends on. A Sales Rep needs at
   * least one account for each of them, so the picker marks the ones still
   * missing instead of leaving it to a rejected save. Defaults to "neither
   * channel required", which is right for a fresh draft with no steps yet.
   */
  requiredChannels?: { email: boolean; linkedin: boolean };
  /**
   * Per-rep "borrow someone else's accounts for this channel" rules picked in
   * the Summary step. A rep covered by one is not missing an account, so the
   * hints below must stay quiet for them.
   */
  channelFallbacks?: ChannelFallback[];
  /**
   * Whether the per-rep "missing account" hints (and the info border that goes
   * with them) may show yet. Held false until the user actually tries to leave
   * the step (LinkedIn-UAT follow-up 2026-09-19): a rep just ticked has had no
   * chance to pick an account, and lighting a hint the instant they are checked
   * reads as being scolded for a change three seconds from finishing. The
   * wizard's goToStep flips this on when a forward move is blocked on a missing
   * account — the same push-forward the error toast always waited for. This only
   * gates DISPLAY; the real, server-mirrored gate in campaign-wizard is
   * unchanged. Defaults false. See [[feedback-validation-ux-hint-then-error]].
   */
  revealAccountGaps?: boolean;
  onEmailAccountsByAieChange: (next: Record<string, string[]>) => void;
  onLinkedinAccountIdsByAieChange: (next: Record<string, string[]>) => void;
  /**
   * `seed` carries account buckets that must land in the SAME update as the
   * assignment change (pre-selecting a just-checked rep's only account) — a
   * second setter call would spread a draft that predates this one.
   */
  onAssignmentsChange: (
    next: SalesRepAssignment[],
    seed?: { emailAccountsByAie?: Record<string, string[]>; linkedinAccountIdsByAie?: Record<string, string[]> },
  ) => void;
  onCrmAutoSyncChange: (enabled: boolean) => void;
}

/** A mailbox as the step lists it (logic/sales-reps.ts merges the two sources). */
type EmailAccountWithAie = MailboxOption;
interface LinkedinAccountWithAie {
  id: string;
  name: string;
  status?: string;
  aiEmployeeId: string | null;
  /** Provider-reported seat features. Absent means unobserved — see linkedin-seat.ts. */
  premiumFeatures?: string[] | null;
}

export const WizCampaignType = stateful((props: WizCampaignTypeProps) => {
  const {
    emailAccountsByAie,
    linkedinAccountIdsByAie,
    aiEmployeeAssignments,
    crmAutoSync,
    assignmentsReady,
    requiredChannels = { email: false, linkedin: false },
    channelFallbacks = [],
    revealAccountGaps = false,
    onEmailAccountsByAieChange,
    onLinkedinAccountIdsByAieChange,
    onAssignmentsChange,
    onCrmAutoSyncChange,
  } = props;

  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);
  const [salesRepsLoaded, setSalesRepsLoaded] = useState(false);
  const [loadingSalesReps, setLoadingSalesReps] = useState(false);

  const [allEmailAccounts, setAllEmailAccounts] = useState<EmailAccountWithAie[]>([]);
  const [allLinkedinAccounts, setAllLinkedinAccounts] = useState<LinkedinAccountWithAie[]>([]);
  const [emailLoaded, setEmailLoaded] = useState(false);
  const [linkedinLoaded, setLinkedinLoaded] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [loadingLinkedin, setLoadingLinkedin] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [linkedinError, setLinkedinError] = useState('');

  // Guards the auto-default effect below so it only ever fires once — see
  // that effect's comment for why the default-pick can't live in here.
  const [autoAssignedDefault, setAutoAssignedDefault] = useState(false);

  const fetchSalesReps = async () => {
    if (salesRepsLoaded) return;
    setLoadingSalesReps(true);
    try {
      const resp = await fetch('/api/ai-employees');
      const data = (await resp.json()) as { ok: boolean; aiEmployees?: SalesRep[] };
      if (data.ok) setSalesReps(data.aiEmployees ?? []);
    } catch {
      /* ignore */
    }
    setSalesRepsLoaded(true);
    setLoadingSalesReps(false);
  };

  const fetchEmailAccounts = async () => {
    if (emailLoaded) return;
    setLoadingEmail(true);
    setEmailError('');
    try {
      // Two sources, one list — the same merge Settings does. Instantly is the
      // retiring path; a Unipile mailbox is the new one and must show here even
      // when Instantly is unconfigured or its plan lapsed (which makes
      // /api/accounts/email answer non-ok), or an email Sales Rep looks unowned.
      const [instantlyRes, unipileRes] = await Promise.all([
        fetch('/api/accounts/email')
          .then(r => r.json())
          .catch(() => ({ ok: false })),
        fetch('/api/accounts/unipile-email')
          .then(r => r.json())
          .catch(() => ({ ok: false })),
      ]);
      // A Unipile mailbox is keyed by the provider's account id, not an address
      // — the same id the launch reads as the Sending Account (logic/sales-reps.ts).
      const { accounts, error } = mailboxOptions(instantlyRes, unipileRes);
      setAllEmailAccounts(accounts);
      setEmailLoaded(true);
      if (error) setEmailError(error);
    } catch (e) {
      setEmailError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoadingEmail(false);
    }
  };

  const fetchLinkedinAccounts = async () => {
    if (linkedinLoaded) return;
    setLoadingLinkedin(true);
    setLinkedinError('');
    try {
      const resp = await fetch('/api/accounts/linkedin');
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = (await resp.json()) as { ok: boolean; accounts: LinkedinAccountWithAie[]; error?: string };
      if (!data.ok) throw new Error(data.error ?? 'Failed to load accounts');
      setAllLinkedinAccounts(data.accounts);
      setLinkedinLoaded(true);
    } catch (e) {
      setLinkedinError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoadingLinkedin(false);
    }
  };

  useEffect(() => {
    void fetchSalesReps();
  }, []);

  // Auto-pick a default Sales Rep — but only for a genuinely empty selection,
  // re-evaluated on the LATEST aiEmployeeAssignments every render (not a
  // value frozen at mount). An earlier version decided this inside
  // fetchSalesReps, a one-shot `useEffect(..., [])` callback that closed over
  // aiEmployeeAssignments as it was AT MOUNT — for an edit-mode load, that's
  // always `[]` (the real assignment arrives moments later, asynchronously,
  // from the campaign's own load effect), so it unconditionally overwrote an
  // already-launched campaign's real Sales Rep with an arbitrary default the
  // instant /api/ai-employees resolved. That was fixed to re-evaluate on every
  // render instead of once at mount — but `assignmentsReady` was still
  // missing: this effect fires as soon as BOTH `/api/ai-employees` (this
  // component's own fetch) AND the parent's separate campaign-load fetch
  // happen to have empty/not-yet-arrived assignments at the same instant,
  // which the two independent requests can easily race on. Without
  // `assignmentsReady`, that race could still silently call
  // `onAssignmentsChange` with an arbitrary default REP on a real, already-
  // launched campaign before its actual Sales Rep had loaded. Waiting for
  // `assignmentsReady` (true immediately for a brand-new draft, true only
  // once the parent's edit-mode load has actually finished otherwise) closes
  // that window entirely — the real assignment is always in hand, or known to
  // be genuinely absent, before this effect ever acts.
  useEffect(() => {
    // The default rep (else the first listed), once, for an empty selection (logic/sales-reps.ts).
    const { decided, assign } = defaultRepToAssign({
      done: autoAssignedDefault,
      ready: assignmentsReady,
      assignments: aiEmployeeAssignments,
      reps: salesReps,
      repsLoaded: salesRepsLoaded,
    });
    if (!decided) return;
    if (assign) onAssignmentsChange(assign);
    setAutoAssignedDefault(true);
  }, [aiEmployeeAssignments, salesReps, salesRepsLoaded, autoAssignedDefault, assignmentsReady]);
  // Always load both account lists — the campaign type is now derived from the
  // Sequence step, so the user might add an Email step at any point and we
  // want the picker to be ready without an extra round-trip.
  useEffect(() => {
    void fetchEmailAccounts();
    void fetchLinkedinAccounts();
  }, []);

  const isAssigned = (id: string) => aiEmployeeAssignments.some(a => a.aiEmployeeId === id);
  const totalPct = aiEmployeeAssignments.reduce((s, a) => s + a.percentage, 0);

  /**
   * Channels this Sales Rep still owes a sending account for — empty when
   * they're good to go. Only channels the sequence actually sends on count,
   * and a rep who borrows someone else's account through a channel fallback is
   * covered too. The server applies exactly this rule, so anything listed here
   * is a save that would come back rejected.
   */
  const missingChannelsForAie = (aieId: string): ('email' | 'linkedin')[] =>
    missingChannelsForRep(aieId, {
      required: requiredChannels,
      emailAccountsByAie,
      linkedinAccountIdsByAie,
      channelFallbacks,
    });

  /**
   * The quiet nudge that replaced this step's old warning-then-error sequence:
   * it appears on the rep the moment they're checked, names the one thing
   * that's missing, and stays out of the way. The error only ever arrives if
   * the user pushes forward without acting on it.
   */
  const missingAccountHint = (missing: ('email' | 'linkedin')[], suffix: string) =>
    missing.length === 0
      ? nothing
      : html`<div class="text-info flex items-center gap-1.5 text-xs">
          <span class="icon-[material-symbols--info-outline] shrink-0 text-sm"></span>
          <span>
            Please select ${missingAccountsPhrase(missing)}
            for this Sales Rep${suffix}.
          </span>
        </div>`;

  // A rep ticked or unticked, a share set by hand, the split evened, an
  // account ticked: logic/sales-reps.ts. The parent callback also prunes the
  // per-rep account buckets of reps no longer assigned in the SAME setDraft
  // call, and the seed for a rep just ticked rides along in it; calling extra
  // setters here would spread a stale draft and overwrite this change.
  const toggleAie = (id: string) => {
    const { assignments, seed } = toggledRep({
      assignments: aiEmployeeAssignments,
      id,
      required: requiredChannels,
      emailAccountsByAie,
      linkedinAccountIdsByAie,
      own: { email: accountsForAieEmail(id).map(a => a.email), linkedin: accountsForAieLinkedin(id).map(a => a.id) },
    });
    if (seed) onAssignmentsChange(assignments, seed);
    else onAssignmentsChange(assignments);
  };

  const setAiePercentage = (id: string, value: number) => {
    const next = withShare(aiEmployeeAssignments, id, value);
    if (next) onAssignmentsChange(next);
  };

  const equaliseSplit = () => onAssignmentsChange(equalShares(aiEmployeeAssignments));

  const toggleEmailForAie = (aieId: string, accountEmail: string) =>
    onEmailAccountsByAieChange(toggledAccount(emailAccountsByAie, aieId, accountEmail));
  const setAllEmailForAie = (aieId: string, emails: string[]) => {
    onEmailAccountsByAieChange({ ...emailAccountsByAie, [aieId]: emails });
  };
  const toggleLinkedinForAie = (aieId: string, accountId: string) =>
    onLinkedinAccountIdsByAieChange(toggledAccount(linkedinAccountIdsByAie, aieId, accountId));
  const setAllLinkedinForAie = (aieId: string, ids: string[]) => {
    onLinkedinAccountIdsByAieChange({ ...linkedinAccountIdsByAie, [aieId]: ids });
  };

  const typeCard = null;
  // (Campaign type cards were removed — the type is now derived from the
  // Sequence step. The variable is kept around the codebase only via the
  // helpers below; no UI is rendered for it any more.)
  void typeCard;

  const salesRepMultiSelector = () => {
    if (loadingSalesReps)
      return html`<div class="flex items-center gap-2 py-4"><span class="loading loading-spinner loading-sm"></span> Loading Sales Representatives...</div>`;
    if (salesReps.length === 0)
      return html`
      <div class="rounded-lg border border-base-300 bg-base-200 p-4 text-center text-sm text-base-content/50">
        No Sales Representative profiles found. Create one in Settings first.
      </div>`;

    const pctValid = aiEmployeeAssignments.length > 0 && totalPct === 100;

    return html`
      <div class="flex flex-col gap-3">
        <p class="text-base-content/70 text-xs">
          Pick one or more Sales Representatives. Each one writes their own messages and uses their own sending accounts.
          The slider controls what share of leads goes to which Sales Representative.
        </p>

        <div class="flex flex-col gap-2">
          ${salesReps.map(emp => {
            const assigned = isAssigned(emp._id);
            const pct = aiEmployeeAssignments.find(a => a.aiEmployeeId === emp._id)?.percentage ?? 0;
            // This is the "highlighted Sales Rep" the pending-changes bar and
            // the error toast both point at, so it has to read as marked
            // without reading as broken — info, not warning. Held back entirely
            // until the user tries to move on (revealAccountGaps): a rep just
            // ticked should not be marked before they can act.
            const missing = assigned && revealAccountGaps ? missingChannelsForAie(emp._id) : [];
            return html`
              <div
                class="rounded-lg border p-3 ${
                  assigned
                    ? missing.length > 0
                      ? 'border-info bg-(--sdr-selected)'
                      : 'border-primary bg-(--sdr-selected)'
                    : 'border-base-300'
                }"
              >
                <label class="flex cursor-pointer items-center gap-3">
                  <input type="checkbox" class="checkbox checkbox-primary checkbox-sm"
                    .checked=${assigned}
                    @change=${() => toggleAie(emp._id)} />
                  <div class="flex flex-1 flex-col">
                    <div class="flex items-center gap-2">
                      <span class="font-medium">${emp.name}</span>
                      ${emp.isDefault ? html`<span class="text-base-content/50 text-xs">Default</span>` : nothing}
                    </div>
                    <span class="text-base-content/60 text-xs">${emp.role} at ${emp.company}</span>
                  </div>
                  ${assigned ? html`<span class="text-sm font-medium tabular-nums">${pct}%</span>` : nothing}
                </label>
                ${
                  assigned
                    ? html`
                  <div class="mt-2 flex items-center gap-3">
                    <input type="range" min="0" max="100" step="1" class="range range-xs flex-1 text-(--sdr-ink-muted)"
                      .value=${String(aiEmployeeAssignments.length === 1 ? 100 : pct)}
                      ?disabled=${aiEmployeeAssignments.length === 1}
                      @input=${(e: Event) => setAiePercentage(emp._id, Number((e.target as HTMLInputElement).value))} />
                    <input type="number" min="0" max="100" class="input input-bordered input-xs w-16"
                      .value=${String(aiEmployeeAssignments.length === 1 ? 100 : pct)}
                      ?disabled=${aiEmployeeAssignments.length === 1}
                      @change=${(e: Event) => setAiePercentage(emp._id, Number((e.target as HTMLInputElement).value))} />
                    ${aiEmployeeAssignments.length === 1 ? html`<span class="text-base-content/50 text-xs">Only Sales Rep — always 100%</span>` : nothing}
                  </div>
                  ${missing.length > 0 ? html`<div class="mt-2">${missingAccountHint(missing, ' below')}</div>` : nothing}
                `
                    : nothing
                }
              </div>
            `;
          })}
        </div>

        ${
          aiEmployeeAssignments.length > 1
            ? html`
          <div class="bg-base-200 flex items-center justify-between rounded-lg px-3 py-2 text-sm">
            <span>
              Total: <span class="font-medium tabular-nums">${totalPct}%</span>
              ${pctValid ? nothing : html` <span class="text-warning">— must equal 100%</span>`}
            </span>
            <button class="btn btn-ghost btn-xs" @click=${equaliseSplit}>
              <span class="icon-[material-symbols--balance]"></span> Equal split
            </button>
          </div>
        `
            : nothing
        }
      </div>
    `;
  };

  const accountsForAieEmail = (aieId: string) => allEmailAccounts.filter(a => a.aiEmployeeId === aieId);
  const accountsForAieLinkedin = (aieId: string) => allLinkedinAccounts.filter(a => a.aiEmployeeId === aieId);

  const emailBlockForAie = (aieId: string) => {
    if (loadingEmail)
      return html`<div class="flex items-center gap-2 py-3"><span class="loading loading-spinner loading-xs"></span> Loading...</div>`;
    if (emailError)
      return html`<div class="alert alert-warning text-sm"><span class="icon-[material-symbols--warning] text-lg"></span> ${emailError}</div>`;
    const accounts = accountsForAieEmail(aieId);
    const selected = emailAccountsByAie[aieId] ?? [];
    if (accounts.length === 0)
      return html`
      <p class="text-base-content/60 text-xs">No email accounts assigned to this Sales Representative. Connect one in Settings.</p>`;
    return html`
      <div class="flex flex-col gap-1.5">
        ${accounts.map(
          account => html`
          <label class="border-base-300 hover:bg-base-200 flex cursor-pointer items-center gap-3 rounded border px-3 py-2 transition">
            <input type="checkbox" class="checkbox checkbox-primary checkbox-xs"
              .checked=${selected.includes(account.email)}
              @change=${() => toggleEmailForAie(aieId, account.email)} />
            <div class="flex flex-col">
              <span class="text-sm font-medium">${account.name}</span>
              <span class="text-base-content/60 text-xs">${account.email}</span>
            </div>
          </label>
        `,
        )}
      </div>
      ${
        accounts.length > 1
          ? html`
        <div class="mt-2 flex gap-2">
          <button class="btn btn-xs" @click=${() =>
            setAllEmailForAie(
              aieId,
              accounts.map(a => a.email),
            )}>Select all</button>
          <button class="btn btn-xs" @click=${() => setAllEmailForAie(aieId, [])}>Clear</button>
        </div>
      `
          : nothing
      }
    `;
  };

  const linkedinBlockForAie = (aieId: string) => {
    if (loadingLinkedin)
      return html`<div class="flex items-center gap-2 py-3"><span class="loading loading-spinner loading-xs"></span> Loading...</div>`;
    if (linkedinError)
      return html`<div class="alert alert-warning text-sm"><span class="icon-[material-symbols--warning] text-lg"></span> ${linkedinError}</div>`;
    const accounts = accountsForAieLinkedin(aieId);
    const selected = linkedinAccountIdsByAie[aieId] ?? [];
    if (accounts.length === 0)
      return html`
      <p class="text-base-content/60 text-xs">No LinkedIn accounts assigned to this Sales Representative. Connect one in Settings.</p>`;
    return html`
      <div class="flex flex-col gap-1.5">
        ${accounts.map(account => {
          // The seat, at the moment the account is actually chosen for a
          // campaign — a free seat can only personalise a limited number of
          // invitations per month, and that is a fact about the pick, not a
          // problem with it. No warning styling, no block.
          const seat = describeSeat(account.premiumFeatures);
          return html`
          <label class="border-base-300 hover:bg-base-200 flex cursor-pointer items-center gap-3 rounded border px-3 py-2 transition">
            <input type="checkbox" class="checkbox checkbox-primary checkbox-xs"
              .checked=${selected.includes(account.id)}
              @change=${() => toggleLinkedinForAie(aieId, account.id)} />
            <span class="text-sm font-medium">${account.name}</span>
            ${toneDot(seatLook(seat), seat.title)}
          </label>
        `;
        })}
      </div>
      ${
        accounts.length > 1
          ? html`
        <div class="mt-2 flex gap-2">
          <button class="btn btn-xs" @click=${() =>
            setAllLinkedinForAie(
              aieId,
              accounts.map(a => a.id),
            )}>Select all</button>
          <button class="btn btn-xs" @click=${() => setAllLinkedinForAie(aieId, [])}>Clear</button>
        </div>
      `
          : nothing
      }
    `;
  };

  const accountsCard = () => {
    if (aiEmployeeAssignments.length === 0) {
      return html`<div class="text-base-content/50 py-3 text-sm">Pick at least one Sales Representative above.</div>`;
    }
    // Both channels are always rendered — the user picks accounts up-front and
    // the actually used channels are determined later by the Sequence builder.
    return html`
      <div class="flex flex-col gap-3">
        ${aiEmployeeAssignments.map(a => {
          const emp = salesReps.find(e => e._id === a.aiEmployeeId);
          if (!emp) return nothing;
          // The hint repeats here because this is where the pick actually
          // happens — the one in the rep list above only says "below".
          // Suppressed when the rep owns no account for that channel at all:
          // the block's own "connect one in Settings" line is the real next
          // step then, and "please select" would be asking for the impossible.
          // Like the rep-list hint, held back until the user tries to move on.
          const missing = revealAccountGaps ? missingChannelsForAie(a.aiEmployeeId) : [];
          return html`
            <div class="rounded-lg border p-3 ${missing.length > 0 ? 'border-info' : 'border-base-300'}">
              <div class="mb-2 flex items-center gap-2">
                <span class="bg-base-200 text-base-content/60 flex h-7 w-7 items-center justify-center rounded">
                  <span class="icon-[material-symbols--smart-toy] text-base"></span>
                </span>
                <span class="font-medium">${emp.name}</span>
                <span class="text-base-content/50 text-xs tabular-nums">${a.percentage}% of leads</span>
              </div>
              ${html`
                <div class="mt-2">
                  <div class="text-base-content/70 mb-1 flex items-center gap-1.5 text-xs font-medium">
                    <span class="icon-[material-symbols--mail] text-base-content/50 text-sm"></span> Email accounts
                  </div>
                  ${
                    missing.includes('email') && accountsForAieEmail(a.aiEmployeeId).length > 0
                      ? html`<div class="mb-1.5">${missingAccountHint(['email'], '')}</div>`
                      : nothing
                  }
                  ${emailBlockForAie(a.aiEmployeeId)}
                </div>
              `}
              ${html`
                <div class="mt-3">
                  <div class="text-base-content/70 mb-1 flex items-center gap-1.5 text-xs font-medium">
                    <span class="icon-[material-symbols--share] text-base-content/50 text-sm"></span> LinkedIn accounts
                  </div>
                  ${
                    missing.includes('linkedin') && accountsForAieLinkedin(a.aiEmployeeId).length > 0
                      ? html`<div class="mb-1.5">${missingAccountHint(['linkedin'], '')}</div>`
                      : nothing
                  }
                  ${linkedinBlockForAie(a.aiEmployeeId)}
                </div>
              `}
            </div>
          `;
        })}
      </div>
    `;
  };

  return html`
    <div class="flex flex-col gap-6">
      <div class="ndi-expand card bg-base-100">
        <div class="card-body p-6">
          <div class="flex items-center gap-3">
            <span class="bg-base-200 text-base-content/60 flex h-10 w-10 items-center justify-center rounded-lg">
              <span class="icon-[material-symbols--groups] text-xl"></span>
            </span>
            <div>
              <h2 class="text-[0.9375rem] font-medium">Sales representatives & split</h2>
              <p class="text-base-content/70 text-sm">Assign one or more Sales Representatives and choose how leads are split between them.</p>
            </div>
          </div>
          <div class="mt-4">${salesRepMultiSelector()}</div>
        </div>
      </div>

      <div class="ndi-expand card bg-base-100">
        <div class="card-body p-6">
          <div class="flex items-center gap-3">
            <span class="bg-base-200 text-base-content/60 flex h-10 w-10 items-center justify-center rounded-lg">
              <span class="icon-[material-symbols--hub] text-xl"></span>
            </span>
            <div>
              <h2 class="text-[0.9375rem] font-medium">Email & LinkedIn accounts per sales representative</h2>
              <p class="text-base-content/70 text-sm">
                Pick the accounts each Sales Representative will use. The actual channels are
                determined by the steps you add in the Sequence builder — unused accounts here
                are simply ignored.
              </p>
            </div>
          </div>
          <div class="mt-4">${accountsCard()}</div>
        </div>
      </div>

      <div class="ndi-expand card bg-base-100">
        <div class="card-body p-6">
          <div class="flex items-center gap-3">
            <span class="bg-base-200 text-base-content/60 flex h-10 w-10 items-center justify-center rounded-lg">
              <span class="icon-[material-symbols--hub] text-xl"></span>
            </span>
            <div>
              <h2 class="text-[0.9375rem] font-medium">CRM sync</h2>
              <p class="text-base-content/70 text-sm">
                Decide how this campaign's leads are mirrored into your connected CRM (e.g. HubSpot).
              </p>
            </div>
          </div>
          <div class="mt-4">
            <label class="flex cursor-pointer items-start gap-3 rounded-lg border border-base-300 bg-base-100 p-4">
              <input
                type="checkbox"
                class="toggle toggle-primary mt-0.5"
                .checked=${!!crmAutoSync}
                @change=${(e: Event) => onCrmAutoSyncChange((e.target as HTMLInputElement).checked)}
              />
              <div class="flex-1">
                <div class="font-medium">Add &amp; update all leads in CRM</div>
                <p class="text-base-content/60 mt-1 text-sm">
                  ${
                    crmAutoSync
                      ? 'ON: every lead (including CSV imports) is automatically pushed to the CRM and kept up to date after each sync.'
                      : 'OFF: CSV-only leads are NOT pushed to the CRM. Leads that originated from the CRM are still kept in sync.'
                  }
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  `;
});
